import React, { useState, useEffect } from 'react';
import { generateThemeFromSeed } from '../utils/colorGenerator';
import { Check, Sparkles, Sun, Moon, Palette } from 'lucide-react';

export const GROUP_COLOR_PRESETS = [
  { name: 'Wölflinge / Meute', hex: '#F57C00', stufe: 'Meute', desc: 'Sonniges Gelb-Orange für die Jüngsten' },
  { name: 'Jungpfadfinder', hex: '#1976D2', stufe: 'Jungpfadfinder', desc: 'Kräftiges Kobaltblau' },
  { name: 'Pfadfinder / Sippe', hex: '#009640', stufe: 'Pfadfinder', desc: 'Klassisches Pfadfinder-Waldgrün' },
  { name: 'Rover', hex: '#D32F2F', stufe: 'Rover', desc: 'Feuriges Glut- & Rubinrot' },
  { name: 'Leiterrunde', hex: '#7B1FA2', stufe: 'Leiterrunde', desc: 'Bordeaux & Edles Amethyst-Lila' },
  { name: 'Kluft & Erde', hex: '#8F4C38', stufe: 'Traditionell', desc: 'Warmes Leder- & Erdbraun' },
  { name: 'Nachtstern-Himmel', hex: '#1E293B', stufe: 'Modern', desc: 'Tiefes Zeltlager-Nachtblau' },
  { name: 'Moos & Nadelwald', hex: '#3E6B27', stufe: 'Natur', desc: 'Naturnahes Moosgrün' }
];

export default function GroupColorPicker({
  value = '#009640',
  onChange,
  groupName = '',
  altersstufe = ''
}) {
  const [seedColor, setSeedColor] = useState(value || '#009640');
  const [previewMode, setPreviewMode] = useState('light'); // 'light' | 'dark'
  const [theme, setTheme] = useState(() => {
    try {
      return generateThemeFromSeed(value || '#009640');
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (value && value !== seedColor) {
      setSeedColor(value);
      try {
        const gen = generateThemeFromSeed(value);
        setTheme(gen);
      } catch (e) {
        console.error('Failed to parse color', e);
      }
    }
  }, [value]);

  const handleColorChange = (newHex) => {
    setSeedColor(newHex);
    try {
      const gen = generateThemeFromSeed(newHex);
      setTheme(gen);
      if (onChange) {
        onChange(newHex, gen);
      }
    } catch (e) {
      console.error('Error generating theme from seed:', e);
      if (onChange) {
        onChange(newHex, null);
      }
    }
  };

  const currentScheme = theme ? (previewMode === 'dark' ? (theme.dark || theme.schemes?.dark) : (theme.light || theme.schemes?.light)) : null;

  return (
    <div className="space-y-4 p-4 sm:p-5 bg-surface-containerLow rounded-2xl border border-outlineVariant/40">
      {/* Header with Title and Mode Switcher */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-outlineVariant/30">
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-primary" />
          <div>
            <h4 className="text-sm font-bold text-surface-on flex items-center gap-1.5">
              <span>MD3 Gruppenfarben-Generator</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono font-bold">
                Material 3
              </span>
            </h4>
            <p className="text-[11px] text-surface-onVariant">
              Generiert eine harmonische, barrierefreie MD3-Palette für diese Stufe.
            </p>
          </div>
        </div>

        {/* Light / Dark Preview Switcher */}
        <div className="flex items-center gap-1 bg-surface-container p-1 rounded-xl border border-outlineVariant/30 text-xs">
          <button
            type="button"
            onClick={() => setPreviewMode('light')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium transition-all ${
              previewMode === 'light'
                ? 'bg-surface-containerLowest text-primary shadow-xs font-bold'
                : 'text-surface-onVariant hover:text-surface-on'
            }`}
          >
            <Sun className="w-3.5 h-3.5 text-amber-500" />
            <span>Hell</span>
          </button>
          <button
            type="button"
            onClick={() => setPreviewMode('dark')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium transition-all ${
              previewMode === 'dark'
                ? 'bg-surface-containerLowest text-primary shadow-xs font-bold'
                : 'text-surface-onVariant hover:text-surface-on'
            }`}
          >
            <Moon className="w-3.5 h-3.5" />
            <span>Dunkel</span>
          </button>
        </div>
      </div>

      {/* Scout Stufen Presets */}
      <div>
        <label className="block text-xs font-bold text-surface-onVariant mb-2 uppercase tracking-wider">
          Pfadfinder Stufen-Vorlagen
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {GROUP_COLOR_PRESETS.map((preset) => {
            const isSelected = seedColor.toLowerCase() === preset.hex.toLowerCase();
            return (
              <button
                key={preset.hex}
                type="button"
                onClick={() => handleColorChange(preset.hex)}
                className={`p-2.5 rounded-xl border text-left flex items-center gap-2.5 transition-all text-xs cursor-pointer ${
                  isSelected
                    ? 'bg-surface-containerHighest border-primary shadow-xs ring-2 ring-primary/20 font-bold'
                    : 'bg-surface-containerLowest border-outlineVariant/40 hover:bg-surface-container hover:border-outlineVariant'
                }`}
              >
                <div
                  className="w-6 h-6 rounded-lg shrink-0 flex items-center justify-center shadow-xs border border-black/10"
                  style={{ backgroundColor: preset.hex }}
                >
                  {isSelected && <Check className="w-3.5 h-3.5 text-white drop-shadow-xs" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-surface-on leading-tight">{preset.name}</div>
                  <div className="text-[10px] font-mono text-surface-onVariant truncate">{preset.hex}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Color Input */}
      <div className="p-3 bg-surface-containerLowest rounded-xl border border-outlineVariant/30 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="color"
              value={seedColor}
              onChange={(e) => handleColorChange(e.target.value)}
              className="w-10 h-10 rounded-xl border-0 cursor-pointer bg-transparent p-0 block"
              title="Klicke um eine freie Farbe zu wählen"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-surface-on">Individuelle Basisfarbe (Seed)</label>
            <span className="text-[11px] text-surface-onVariant">Klicke auf das Farbfeld oder tippe den Hex-Code ein.</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={seedColor}
            onChange={(e) => {
              const val = e.target.value;
              if (val.startsWith('#') && val.length <= 7) {
                handleColorChange(val);
              } else if (!val.startsWith('#') && val.length <= 6) {
                handleColorChange('#' + val);
              }
            }}
            className="w-24 px-3 py-1.5 bg-surface-containerLow border border-outlineVariant/50 rounded-xl text-xs font-mono text-surface-on text-center uppercase font-bold focus:outline-none focus:border-primary"
            placeholder="#009640"
          />
        </div>
      </div>

      {/* Generated MD3 Harmonies Swatches */}
      {currentScheme && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-surface-onVariant uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span>MD3 Tonal-Harmonien ({previewMode === 'light' ? 'Heller Modus' : 'Dunkler Modus'})</span>
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* Primary */}
            <div
              className="p-2.5 rounded-xl border border-black/5 shadow-2xs transition-colors"
              style={{ backgroundColor: currentScheme.primaryContainer, color: currentScheme.onPrimaryContainer }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">Hauptton</span>
                <div className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: currentScheme.primary }} />
              </div>
              <div className="text-xs font-bold truncate">Primary</div>
              <div className="text-[10px] font-mono opacity-80">{currentScheme.primary}</div>
            </div>

            {/* Secondary */}
            <div
              className="p-2.5 rounded-xl border border-black/5 shadow-2xs transition-colors"
              style={{ backgroundColor: currentScheme.secondaryContainer, color: currentScheme.onSecondaryContainer }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">Sekundär</span>
                <div className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: currentScheme.secondary }} />
              </div>
              <div className="text-xs font-bold truncate">Secondary</div>
              <div className="text-[10px] font-mono opacity-80">{currentScheme.secondary}</div>
            </div>

            {/* Tertiary */}
            <div
              className="p-2.5 rounded-xl border border-black/5 shadow-2xs transition-colors"
              style={{ backgroundColor: currentScheme.tertiaryContainer, color: currentScheme.onTertiaryContainer }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">Akzent</span>
                <div className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: currentScheme.tertiary }} />
              </div>
              <div className="text-xs font-bold truncate">Tertiary</div>
              <div className="text-[10px] font-mono opacity-80">{currentScheme.tertiary}</div>
            </div>

            {/* Surface */}
            <div
              className="p-2.5 rounded-xl border border-black/5 shadow-2xs transition-colors"
              style={{ backgroundColor: currentScheme.surfaceContainerHigh || currentScheme.surfaceContainer, color: currentScheme.onSurface }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">Container</span>
                <div className="w-3 h-3 rounded-full border border-black/10" style={{ backgroundColor: currentScheme.surface }} />
              </div>
              <div className="text-xs font-bold truncate">Surface</div>
              <div className="text-[10px] font-mono opacity-80">{currentScheme.surface}</div>
            </div>
          </div>
        </div>
      )}

      {/* Live Group Badge & Card Preview */}
      {currentScheme && (
        <div
          className="p-4 rounded-2xl border transition-colors space-y-3"
          style={{
            backgroundColor: currentScheme.surface,
            borderColor: currentScheme.outlineVariant || 'rgba(0,0,0,0.12)',
            color: currentScheme.onSurface
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center font-bold text-sm shadow-xs"
                style={{ backgroundColor: currentScheme.primary, color: currentScheme.onPrimary }}
              >
                ⚜️
              </div>
              <div>
                <div className="text-xs font-bold font-heading">
                  {groupName || 'Vorschau der Gruppe'}
                </div>
                <div className="text-[10px] opacity-75">
                  {altersstufe || 'Altersstufe'}
                </div>
              </div>
            </div>

            <span
              className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
              style={{ backgroundColor: currentScheme.primaryContainer, color: currentScheme.onPrimaryContainer }}
            >
              Stufen-Badge
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1 border-t" style={{ borderColor: currentScheme.outlineVariant || 'rgba(0,0,0,0.08)' }}>
            <span
              className="text-xs px-3 py-1 rounded-full font-bold shadow-2xs"
              style={{ backgroundColor: currentScheme.primary, color: currentScheme.onPrimary }}
            >
              Gruppenstunde
            </span>

            <span
              className="text-xs px-3 py-1 rounded-full font-semibold"
              style={{ backgroundColor: currentScheme.secondaryContainer, color: currentScheme.onSecondaryContainer }}
            >
              Aktionen &amp; Fahrten
            </span>

            <span
              className="text-xs px-3 py-1 rounded-full font-semibold"
              style={{ backgroundColor: currentScheme.tertiaryContainer, color: currentScheme.onTertiaryContainer }}
            >
              🔥 Lagerfeuer
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
